<template>
  <u-form
    :state="state"
    :schema="schema"
    class="space-y-4"
    @submit="signIn"
  >
    <h1>Sign in</h1>
    <dynamic-form-field
      v-model="state.email"
      field-key="email"
      :input="input"
    />
    <dynamic-form-field
      v-model="state.password"
      field-key="password"
      :input="input"
    >
      <template #hint>
        <u-button
          variant="link"
          class="p-0 text-sm"
          to="/recovery"
          data-testid="recovery-link"
          tabindex="-1"
        >
          Forgot your password?
        </u-button>
      </template>
    </dynamic-form-field>
    <div class="ml-auto flex flex-col items-end gap-4 md:flex-row">
      <u-button
        type="submit"
        data-testid="submit"
        :disabled="app.state.isLoading || !isValid"
        :loading="isSigningIn"
      >
        Sign in
      </u-button>
      <u-button
        v-if="
          schema
            .omit({
              password: true,
            })
            .safeParse(state).success
        "
        type="button"
        color="white"
        data-testid="magic"
        :disabled="app.state.isLoading"
        :loading="isMagicLinkSigningIn"
        @click="signInWithOTP"
      >
        Send magic link
      </u-button>
    </div>
    <p
      class="w-full text-center font-bold text-tsppink-500 dark:text-tsppink-400"
    >
      Don't have an account?
      <u-button
        variant="link"
        class="p-0 font-bold"
        to="/sign-up"
        data-testid="sign-up-link"
      >
        Sign up
      </u-button>
    </p>
  </u-form>
</template>

<script lang="ts" setup>
import { useForm } from '@tarcltd/form-vue'

definePageMeta({
  middleware: ['unauth'],
  layout: 'sign-in',
})

useHead({
  title: 'The Show Portal - Sign In',
})

const supabase = useSupabaseClient<Supabase>()
const app = useApp()
const toast = useToast()
const { state, schema, input, isValid } = useForm({
  type: 'object',
  properties: {
    email: {
      type: 'string',
      name: 'Email',
      format: 'email',
      attrs: {
        'field:type': 'Text',
      },
    },
    password: {
      type: 'string',
      name: 'Password',
      minLength: 8,
      attrs: {
        'field:type': 'Text',
        'elementInput': {
          type: 'password',
        },
      },
    },
  },
  required: ['email', 'password'],
})
const isSigningIn = ref(false)
const isMagicLinkSigningIn = ref(false)

/**
 * Validate the sign in form and pass the email and password to the user store.
 */
async function signIn() {
  try {
    isSigningIn.value = true
    app.state.isLoading = true

    const { status_code } = await $fetch('/api/v1/precheck/user', {
      params: {
        email: state.email,
      },
    })

    if (status_code === 404) {
      toast.add({
        color: 'red',
        title: 'It doesn\'t look like you have an account.',
        actions: [
          {
            label: 'Sign up',
            to: '/sign-up',
            icon: 'i-ph-sparkle',
          },
        ],
      })
    }
    else {
      const { error } = await supabase.auth.signInWithPassword({
        email: state.email,
        password: state.password,
      })

      if (error) {
        throw error
      }

      const showToEnter = useCookie('the-show-portal-show-to-enter')

      if (showToEnter.value) {
        await navigateTo(`/event/${showToEnter.value}/enter`)

        showToEnter.value = null
      }
      else {
        await navigateTo('/')
      }
    }
  }
  catch (error) {
    console.error(error)

    toast.add({
      color: 'red',
      title: 'Could not sign in.',
      description: 'Please check your email and password and try again.',
    })

    isSigningIn.value = false
  }
  finally {
    isSigningIn.value = false
    app.state.isLoading = false
  }
}

async function signInWithOTP() {
  try {
    isMagicLinkSigningIn.value = true
    app.state.isLoading = true

    const { data, status_code } = await $fetch('/api/v1/precheck/magic-link', {
      method: 'POST',
      body: {
        email: state.email,
        origin: window.location.origin,
      },
    })

    if (data) {
      toast.add({
        color: 'green',
        title: 'Magic link sent!',
        description: 'Please check your email for a magic link.',
      })
    }
    else if (status_code === 404) {
      toast.add({
        color: 'red',
        title: 'It doesn\'t look like you have an account.',
        actions: [
          {
            label: 'Sign up',
            to: '/sign-up',
            icon: 'i-ph-sparkle',
          },
        ],
      })
    }
    else {
      throw new Error('Could not send magic link.')
    }
  }
  catch (error) {
    console.error(error)

    toast.add({
      color: 'red',
      title: 'Could not send magic link.',
      description: 'Please check your email and try again.',
    })
  }
  finally {
    app.state.isLoading = false
    isMagicLinkSigningIn.value = false
  }
}
</script>
